"use client";

import Link from "next/link";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import type { FaqEntry } from "@/lib/cms";

type Props = { faqs: FaqEntry[] };

const FAQ = ({ faqs }: Props) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<"business" | "individual">("individual");

  const lang = (i18n.language?.split("-")[0] || "en") as "en" | "es";
  const fallback = "en" as const;

  const filterFor = (audience: "business" | "individual") => {
    const matched = faqs.filter((f) => f.audience === audience && f.language === lang);
    if (matched.length > 0) return matched;
    return faqs.filter((f) => f.audience === audience && f.language === fallback);
  };

  const activeFaqs = filterFor(activeTab);

  return (
    <section className="bg-white py-20 px-6 lg:px-16" id="faq">
      <div className="max-w-[800px] mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="font-heading font-bold text-2xl lg:text-[36px] text-foreground mb-2">
            {t("faq.heading")}
          </h2>
          <p className="font-body text-base text-foreground/70">
            {t("faq.subtitle")}
          </p>
        </div>

        {/* Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex rounded-full border border-accent-blue overflow-hidden">
            <button
              onClick={() => setActiveTab("individual")}
              className={`font-button text-sm px-5 py-2.5 transition-all duration-200 ${
                activeTab === "individual"
                  ? "bg-accent-blue text-white"
                  : "bg-transparent text-accent-blue hover:bg-accent-blue/10"
              }`}
            >
              {t("faq.tabIndividual")}
            </button>
            <button
              onClick={() => setActiveTab("business")}
              className={`font-button text-sm px-5 py-2.5 transition-all duration-200 ${
                activeTab === "business"
                  ? "bg-accent-blue text-white"
                  : "bg-transparent text-accent-blue hover:bg-accent-blue/10"
              }`}
            >
              {t("faq.tabBusiness")}
            </button>
          </div>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="mb-8">
          {activeFaqs.map((faq, index) => (
            <AccordionItem
              key={`${activeTab}-${faq.slug}-${index}`}
              value={`${activeTab}-${index}`}
              className="bg-surface-card rounded-xl mb-3 px-5 border-l-4 border-l-accent-blue border-t-0 border-r-0 border-b-0 shadow-sm"
            >
              <AccordionTrigger className="font-heading font-medium text-sm text-foreground hover:text-accent-blue hover:no-underline py-4 text-left">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="font-body text-xs text-foreground/70 pb-4 text-left whitespace-pre-wrap">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* CTA */}
        <div className="text-center">
          <Link href="/faqs">
            <Button
              variant="outline"
              className="border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white rounded-full px-6 py-3 font-button text-sm cta-hover"
            >
              {t("faq.cta")}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
